// SPDX-FileCopyrightText: Copyright 2023 Dash0 Inc.

"use client";

import { 
	Portal as TooltipPrimitivePortal,
	Provider as TooltipPrimitiveProvider, 
	Root as TooltipPrimitiveRoot, 
	Trigger as TooltipPrimitiveTrigger, 
	Content as TooltipPrimitiveContent
} from "@radix-ui/react-tooltip";


import { getCustomPropertiesForInsetSupport } from "@/components/ui/inset";
import { cn } from "@/lib/utils";
import React from "react";

const TooltipProvider = TooltipPrimitiveProvider;

const Tooltip = TooltipPrimitiveRoot as typeof TooltipPrimitiveRoot & {
	Trigger: typeof TooltipPrimitiveTrigger;
	Content: typeof TooltipPrimitiveContent;
};

const TooltipTrigger = TooltipPrimitiveTrigger;
Tooltip.Trigger = TooltipTrigger;

const tooltipContentInsetStyles = getCustomPropertiesForInsetSupport("0.25rem", "0.25rem", "0.5rem", "0.5rem");

const TooltipContent = React.forwardRef<
	React.ElementRef<typeof TooltipPrimitiveContent>,
	React.ComponentPropsWithoutRef<typeof TooltipPrimitiveContent>
>(({ className, sideOffset = 4, children, ...props }, ref) => {
	if (!children) {
		return null;
	}

	return (
		<TooltipPrimitivePortal>
			<TooltipPrimitiveContent
				ref={ref}
				sideOffset={sideOffset}
				className={cn(
					"z-50 overflow-hidden rounded-md border border-default bg-hover-card px-2 py-1 text-sm font-medium leading-5 text-default shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
					className
				)}
				style={tooltipContentInsetStyles}
				{...props}
			>
				{children}
			</TooltipPrimitiveContent>
		</TooltipPrimitivePortal>
	);
});
TooltipContent.displayName = TooltipPrimitiveContent.displayName;
Tooltip.Content = TooltipContent;

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger };
